import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useGet from '../Hooks/useGet';
import API_ENDPOINTS from '../Config/api';
import { fetchWithAuth } from '../Utils/fetchWithAuth';
import ListOrdiniTable from '../Components/Table/ListOrdiniTable';

export default function MsspOrdersSummary() {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [month, setMonth] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const apiUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    if (month) params.append('month', month);
    if (sortOrder) params.append('sortOrder', sortOrder);
    const qs = params.toString();
    return qs ? `${API_ENDPOINTS.myOrdiniMSSP}?${qs}` : API_ENDPOINTS.myOrdiniMSSP;
  }, [fromDate, toDate, month, sortOrder]);

  const { fetchedData, refetch } = useGet(apiUrl);

  React.useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('🔍 MsspOrdersSummary - API URL changed:', apiUrl);
  }, [apiUrl]);

  const orders = fetchedData ?? [];

  // Debug logs to help diagnose filtering issues in browser console
  // These logs will show number of fetched orders and current filters
  // Remove or disable in production
  if (typeof window !== 'undefined') {
    try {
      // eslint-disable-next-line no-console
      console.debug('MsspOrdersSummary: fetched orders count', Array.isArray(orders) ? orders.length : 0);
      // eslint-disable-next-line no-console
      console.debug('MsspOrdersSummary: current filters', { month, fromDate, toDate, sortOrder });
      // eslint-disable-next-line no-console
      console.debug('MsspOrdersSummary: current API URL', apiUrl);
    } catch (e) {
      // ignore
    }
  }

  const filteredAndSorted = useMemo(() => {
    // The API now returns already-filtered results according to the query params.
    // Here we only ensure sorting client-side as a fallback.
    const list = Array.isArray(orders) ? [...orders] : [];
    list.sort((a, b) => {
      const sa = a.data_creazione || a.data_rinnovo || a.data_pagamento || '';
      const sb = b.data_creazione || b.data_rinnovo || b.data_pagamento || '';
      const ta = sa ? Date.parse(sa) : 0;
      const tb = sb ? Date.parse(sb) : 0;
      return sortOrder === 'asc' ? ta - tb : tb - ta;
    });
    return list;
  }, [orders, sortOrder]);

  // Esporta i dati filtrati chiedendo al server un PDF e scaricandolo (nessuna finestra né stampa automatica)
  const handleExportPdf = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('fromDate', fromDate);
      if (toDate) params.append('toDate', toDate);
      if (sortOrder) params.append('sortOrder', sortOrder);
      if (month) params.append('month', month);
      // Add timestamp to force refresh and bypass browser cache
      params.append('_t', Date.now());

      // Always call backend API host (no CRA proxy). Reuse the same base as data list.
      const url = `${API_ENDPOINTS.myOrdiniMSSP}export-pdf/?${params.toString()}`;
      const resp = await fetchWithAuth(url, { method: 'GET', cache: 'no-store' });

      if (!resp.ok) {
        const text = await resp.text();
        console.error('Export PDF failed:', resp.status, text);
        alert('Errore durante l\'esportazione del PDF');
        return;
      }

      const ct = resp.headers.get('content-type') || '';
      if (!ct.toLowerCase().includes('application/pdf')) {
        const text = await resp.text();
        console.error('Export PDF unexpected content-type:', ct, text);
        alert('Errore durante l\'esportazione del PDF (risposta non-PDF).');
        return;
      }

      const blob = await resp.blob();
      const urlBlob = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlBlob;
      const now = new Date().toISOString().slice(0,19).replace(/[:T]/g,'-');
      a.download = `riepilogo_ordini_${now}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(urlBlob);
      a.remove();
    } catch (e) {
      console.error('Errore export PDF:', e);
      alert('Errore durante l\'esportazione del PDF');
    }
  }, [fromDate, toDate, sortOrder, month]);

  const handleBack = useCallback(() => {
    try {
      // Se esiste una history del browser usa navigate(-1), altrimenti fai fallback a route fissa
      if (typeof window !== 'undefined' && window.history && window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/ordini');
      }
    } catch (e) {
      navigate('/ordini');
    }
  }, [navigate]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="px-3 py-2 bg-white text-gray-800 shadow-md hover:shadow-lg rounded-md"
          >
            ← Indietro
          </button>
          <h2 className="text-2xl font-semibold">Riepilogo Ordini</h2>
        </div>
        <div />
      </div>

      <div>
        {/* Pass the filters UI as middleContent so it is rendered between search and buttons */}
        <ListOrdiniTable
          data={filteredAndSorted}
          setData={() => {}}
          refetchData={refetch}
          searchShrinkCm={1.5}
          exportHandler={handleExportPdf}
          showCreateButton={false}
          middleContent={(
            <div className="flex items-center gap-3">
              <label className="text-sm">Mese</label>
              <input type="month" value={month} onChange={(e) => { setMonth(e.target.value); setFromDate(''); setToDate(''); }} className="px-2 py-1 border rounded" />
              <label className="text-sm">Da</label>
              <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setMonth(''); }} className="px-2 py-1 border rounded" />
              <label className="text-sm">A</label>
              <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setMonth(''); }} className="px-2 py-1 border rounded" />
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="px-2 py-1 border rounded">
                <option value="desc">Più recenti</option>
                <option value="asc">Più vecchi</option>
              </select>
              <button onClick={() => { setFromDate(''); setToDate(''); setMonth(''); setSortOrder('desc'); }} className="px-2 py-1 bg-gray-100 rounded">Reset</button>
            </div>
          )}
        />
      </div>
    </div>
  );
}
